import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

export type ProductGridViewMode = 'grid' | 'list';

@Component({
  selector: 'app-product-grid',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './product-grid.component.html',
  styleUrls: ['./product-grid.component.scss'],
})
export class ProductGridComponent implements OnChanges {
  /** Products to display — same model used by DashboardComponent */
  @Input() products: any[] = [];

  /** Label shown above the grid e.g. "All Items (24)" */
  @Input() categoryLabel = 'All Items';

  /** Whether to show total count badge */
  @Input() showCount = true;

  /** Current cart items — passed from parent to drive the stepper visibility */
  @Input() cartItems: any[] = [];

  /** When true show sellingpriceincltax; when false (default) show sellingprice */
  @Input() isUnitPriceInclTax: boolean = false;

  /** Emits the product object when the user clicks a product card */
  @Output() productClicked = new EventEmitter<any>();

  /** Emitted when the user taps + on a card stepper */
  @Output() increaseQty = new EventEmitter<any>();

  /** Emitted when the user taps − on a card stepper */
  @Output() decreaseQty = new EventEmitter<any>();

  readonly DEFAULT_IMAGE = 'assets/no-image-removebg-preview.png';

  viewMode: ProductGridViewMode = 'grid';
  displayedProducts: any[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['products']) {
      this.displayedProducts = this.products ?? [];
    }
  }

  setViewMode(mode: ProductGridViewMode): void {
    this.viewMode = mode;
  }

  onProductClick(product: any): void {
    this.productClicked.emit(product);
  }

  /** Resolves which price to display on the card */
  getDisplayPrice(product: any): number {
    console.log(this.products,'fresms selling')
    if (this.isUnitPriceInclTax) {
      
      return product.product_selling_price_incl ?? product.sellingpriceincltax ?? product.sellingprice ?? 0;
    }
    return product.sellingprice ?? product.sellingpriceincltax ?? 0;
  }

  /** Resolves image URL with standard fallback */
  getProductImage(product: any): string {
    return product?.image || product?.imgpath || this.DEFAULT_IMAGE;
  }

  /** Falls back gracefully to standard image when loading fails */
  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img && !img.src.includes(this.DEFAULT_IMAGE)) {
      img.src = this.DEFAULT_IMAGE;
    }
  }

  /** Resolves total stock to display */
  getDisplayStock(product: any): string {
    if (product.product_type === 'Non Stock') {
      return 'Unlimited Stock';
    }
    const stockQty = product.qty ?? product.quantity ?? product.stock;
    if (stockQty === null || stockQty === undefined) {
      return 'Stock: N/A';
    }
    return `Stock: ${stockQty}`;
  }

  /** Resolves stock status CSS class for badge */
  getStockStatusClass(product: any): string {
    if (product.product_type === 'Non Stock') {
      return 'in-stock';
    }
    const q = product.qty ?? product.quantity ?? product.stock;
    if (q === null || q === undefined || q > 5) {
      return 'in-stock';
    }
    if (q > 0) {
      return 'low-stock';
    }
    return 'out-of-stock';
  }

  /** Resolves badge label text */
  getStockLabel(product: any): string {
    if (product.product_type === 'Non Stock') {
      return 'In Stock';
    }
    const q = product.qty ?? product.quantity ?? product.stock;
    if (q === null || q === undefined) {
      return 'In Stock';
    }
    if (q <= 0) {
      return 'Out of Stock';
    }
    return `Stock: ${q}`;
  }

  trackByProductId(_: number, product: any): any {
    return product.productId ?? product.psid ?? product.id;
  }

  /**
   * Returns the cart quantity of `product`.
   * Uses `increaseQuantity` as the primary field — the same field that
   * bill panel's getProductQty() reads — so both panels stay in sync.
   * Returns 0 when the product is not in the cart → shows Add to Cart button.
   */
  getCartQty(product: any): number {
  return this.getCartItem(product)?.increaseQuantity ?? 0;  }

  /**
   * Returns the actual CART ITEM reference for `product`.
   * This must be emitted on + / − clicks so the dashboard's
   * increaseQuantity() / decreaseQuantity() mutate the correct object.
   * Emitting the catalog product would be a different reference and
   * the mutation would have no effect on the cart.
   */
  getCartItem(product: any): any {
    if (!this.cartItems?.length) return null;
    return this.cartItems.find(
      (item) =>
        (item.psid && item.psid === product.psid) ||
        (item.productId && item.productId === product.productId) ||
        (item.id && item.id === product.id)
    ) ?? null;
  }
}
